import cx from 'classnames';
import { memo, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { DivTooltip } from '@wg/wows-react-uikit';

import { root } from '~/preloaded';
import dwhExport from '~/dwhExport';

import { ROUTES_BADGES_TYPES } from '~/router/routes';

import { CLAN_HUB_TILE_SIZE } from './ClanHubTile';
import styles from './ClanHubTile.module.scss';
import ClanHubTileCountdown from './ClanHubTileCountdown';
import ClanHubTileTooltip from './ClanHubTileTooltip';

import type { HubTileModel } from '../clanHub.tiles.build';
import type { ROLE_NAMES } from '~/roles';

interface ClanHubTileContentProps {
  tile: HubTileModel;
  size: CLAN_HUB_TILE_SIZE;
  currentRoleName?: ROLE_NAMES | null;
  onClick?: () => void;
}

const ClanHubTileContent = ({ tile, size, currentRoleName = null, onClick }: ClanHubTileContentProps) => {
  const isSizeSmall = size === CLAN_HUB_TILE_SIZE.SMALL;
  const isSizeLarge = size === CLAN_HUB_TILE_SIZE.LARGE;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (!tile.enabled) {
      e.preventDefault();
      return;
    }

    if (tile.dwhEvents?.click) {
      dwhExport.push(tile.dwhEvents.click);
    }

    onClick?.();
  };

  const { labelNewCount, isRewardAvailable } = useMemo(() => {
    const labelNewCount = tile.badgesResolved?.reduce<string | number | undefined>((acc, badge) => {
      if (badge.kind !== ROUTES_BADGES_TYPES.COUNTER) {
        return acc;
      }

      if (badge.value === '99+' || acc === '99+') {
        return '99+';
      }

      return typeof acc === 'number' ? acc + badge.value : badge.value;
    }, undefined);

    const isRewardAvailable =
      tile.badgesResolved?.some((badge) => {
        if (badge.kind === ROUTES_BADGES_TYPES.REWARD) {
          return true;
        }

        return false;
      }) ?? false;

    return { labelNewCount, isRewardAvailable };
  }, [tile.badgesResolved]);

  return (
    <DivTooltip
      className={styles.tile}
      tooltipBody={
        <ClanHubTileTooltip tile={tile} currentRoleName={currentRoleName} isRewardAvailable={isRewardAvailable} />
      }
    >
      <NavLink
        to={`${root}${tile.toResolved}`}
        className={cx(styles.tileContent, {
          [styles.small]: isSizeSmall,
          [styles.large]: isSizeLarge,
          [styles.disabled]: !tile.enabled,
          [styles.highlighted]: isRewardAvailable,
        })}
        onClick={handleClick}
        key={tile.id}
      >
        {labelNewCount && <span className={styles.tileLabelNew}>{labelNewCount}</span>}
        {tile.icon && (
          <span className={styles.tileIcon} style={{ '--icon-url': `url(${tile.icon})` } as React.CSSProperties} />
        )}
        <div className={styles.tileData}>
          <div className={styles.tileTitle}>{tile.title}</div>
          {isSizeLarge && tile.subtitle && <div className={styles.tileSubtitle}>{tile.subtitle}</div>}
          {tile.endsAtResolved != null && tile.endsAtResolved > Date.now() ? (
            <ClanHubTileCountdown
              endsAtResolved={tile.endsAtResolved}
              endsAtTextLabelResolved={tile.endsAtTextLabelResolved}
            />
          ) : isSizeSmall && tile.subtitle ? (
            <div className={styles.tileSubtitle}>{tile.subtitle}</div>
          ) : null}
        </div>
      </NavLink>
    </DivTooltip>
  );
};

export default memo(ClanHubTileContent);
